import React, { useEffect, useState } from 'react'
import { Box, Typography, Button, Grid, Card, CardContent, TextField, Chip, CircularProgress, Alert, Dialog, DialogTitle, DialogContent, DialogActions, FormControlLabel, Switch, Divider, Tabs, Tab, Select, MenuItem, FormControl, InputLabel, List, ListItem, ListItemText } from '@mui/material'
import { alpha, useTheme } from '@mui/material/styles'
import * as billingSx from './adminBillingSettingsThemeSx'
import { ArrowBack as ArrowBackIcon, Add as AddIcon, Settings as SettingsIcon, Refresh as RefreshIcon, Edit as EditIcon, MonetizationOn as MonetizationOnIcon, Sync as SyncIcon, AccountBalance, Delete as DeleteIcon, Warning as WarningIcon } from '@mui/icons-material'
import billingService, { BillingTier, CompanyBillingAccount, BillingCalculation, PaymentMethod, BillingHistory } from '../../../../services/BillingService'
import permissionService, { PermissionDefinitionsByCategory, TierPermissions } from '../../../../services/PermissionService'
import TierPermissionsEditor from '../../PermissionEditor/TierPermissionsEditor'
import { CompanyService } from '../../../../services/CompanyService'
import { useAppSelector } from '../../../../hooks/useAppSelector'
import { SEVV_TABS_OVERFLOW_SX } from '../../../../theme/sevvTabsOverflowSx'

interface AdminBillingSettingsProps {
  onSectionChange: (section: any) => void
}

const AdminBillingSettings: React.FC<AdminBillingSettingsProps> = ({ onSectionChange }) => {
  const theme = useTheme()
  const defaultTierColor = theme.palette.primary.main
  const [loading, setLoading] = useState(true)
  const [tiers, setTiers] = useState<BillingTier[]>([])
  const [error, setError] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState<Partial<BillingTier>>({
    name: '',
    tier_key: '',
    description: '',
    base_price: undefined as any,
    price_per_user: undefined as any,
    free_users_limit: undefined as any,
    max_users: undefined as any,
    max_rooms: undefined as any,
    features: {},
    color: defaultTierColor,
    is_default: false,
    is_active: true,
    requires_sales_contact: false,
    sales_contact_message: '',
  })

  const [editOpen, setEditOpen] = useState(false)
  const [editTier, setEditTier] = useState<BillingTier | null>(null)
  const [editForm, setEditForm] = useState<Partial<BillingTier>>({})
  const [editTab, setEditTab] = useState<number>(0)
  const [permDefs, setPermDefs] = useState<PermissionDefinitionsByCategory | null>(null)
  const [tierPerms, setTierPerms] = useState<TierPermissions>({})
  const [loadingPerms, setLoadingPerms] = useState<boolean>(false)
  const [migrationStatus, setMigrationStatus] = useState<any>(null)
  const [migrating, setMigrating] = useState(false)
  const [subscriptionCounts, setSubscriptionCounts] = useState<any>({})
  const [loadingSubscriptions, setLoadingSubscriptions] = useState(false)
  const [syncingTier, setSyncingTier] = useState<string | null>(null)
  const [formRefreshKey, setFormRefreshKey] = useState(0)
  
  // Delete tier dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [tierToDelete, setTierToDelete] = useState<BillingTier | null>(null)
  const [replacementTierId, setReplacementTierId] = useState<string>('')
  const [deleting, setDeleting] = useState(false)
  const [affectedCompaniesInfo, setAffectedCompaniesInfo] = useState<{
    tier_id?: string
    tier_key?: string
    tier_name?: string
    affected_count?: { billing_accounts: number; legacy_companies: number; total: number }
    affected_companies?: Array<{ id: string; name: string }>
    can_delete_safely?: boolean
  } | null>(null)
  const [loadingAffectedCompanies, setLoadingAffectedCompanies] = useState(false)
  
  // Company billing data state
  const [currentCompany, setCurrentCompany] = useState<any>(null)
  const [billingAccount, setBillingAccount] = useState<CompanyBillingAccount | null>(null)
  const [billingCalculation, setBillingCalculation] = useState<BillingCalculation | null>(null)
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([])
  const [billingHistory, setBillingHistory] = useState<BillingHistory[]>([])
  const [upcomingInvoice, setUpcomingInvoice] = useState<any>(null)
  const [subscriptions, setSubscriptions] = useState<any[]>([])
  const [loadingBillingData, setLoadingBillingData] = useState(false)
  const [billingDataError, setBillingDataError] = useState<string | null>(null)
  
  // Get current user from Redux store
  const userData = useAppSelector((state) => state.user.userData)

  const load = async () => {
    try {
      setLoading(true)
      setError(null)
      
      const res = await billingService.getBillingTiers(false)
      console.log('Server response:', res)
      if (res.success) {
        console.log('Setting tiers from server:', res.billing_tiers)
        setTiers(res.billing_tiers)
      } else {
        throw new Error('Failed to load billing tiers')
      }
    } catch (e: any) {
      setError(`Failed to load billing tiers: ${e.message}`)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { 
    load()
    loadMigrationStatus()
    loadSubscriptionCounts()
    loadCurrentCompany()
  }, [])

  const loadMigrationStatus = async () => {
    try {
      const result = await billingService.getBillingTierMigrationStatus()
      if (result.success) {
        setMigrationStatus(result.status)
      }
    } catch (error) {
      console.error('Error loading migration status:', error)
    }
  }

  const loadCurrentCompany = async () => {
    try {
      const result = await CompanyService.getCurrentUserCompany()
      if (result.success && result.company) {
        setCurrentCompany(result.company)
        // Load billing data for this company
        await loadCompanyBillingData(result.company.id)
      } else {
        console.warn('No company found for current user')
      }
    } catch (error) {
      console.error('Error loading current company:', error)
    }
  }

  const loadCompanyBillingData = async (companyId: string) => {
    try {
      setLoadingBillingData(true)
      setBillingDataError(null)
      
      const result = await billingService.getComprehensiveBillingData(companyId, {
        include_history: true,
        history_limit: 10,
        include_upcoming: true
      })
      
      if (result.success) {
        setBillingAccount(result.billing_account || null)
        setBillingCalculation(result.billing_calculation || null)
        setPaymentMethods(result.payment_methods || [])
        setBillingHistory(result.billing_history || [])
        setUpcomingInvoice(result.upcoming_invoice || null)
        setSubscriptions(result.subscriptions || [])
      } else {
        setBillingDataError(result.error || 'Failed to load billing data')
      }
    } catch (error: any) {
      console.error('Error loading company billing data:', error)
      setBillingDataError(error.message || 'Failed to load billing data')
    } finally {
      setLoadingBillingData(false)
    }
  }

  const refreshBillingData = async () => {
    if (currentCompany?.id) {
      await loadCompanyBillingData(currentCompany.id)
    }
  }

  // Listen for billing tier changes from other components
  useEffect(() => {
    const handleBillingTierChange = () => {
      console.log('Billing tier change detected, refreshing billing data...')
      refreshBillingData()
    }

    // Listen for custom events when billing tier changes
    window.addEventListener('billingTierChanged', handleBillingTierChange)
    
    return () => {
      window.removeEventListener('billingTierChanged', handleBillingTierChange)
    }
  }, [currentCompany?.id])


  const loadSubscriptionCounts = async () => {
    try {
      setLoadingSubscriptions(true)
      const res = await billingService.getSubscriptionCounts()
      if (res.success) {
        setSubscriptionCounts(res.data || {})
      }
    } catch (error) {
      console.error('Error loading subscription counts:', error)
      setSubscriptionCounts({})
    } finally {
      setLoadingSubscriptions(false)
    }
  }

  const syncIndividualTier = async (tierId: string, tierName: string) => {
    try {
      setSyncingTier(tierId)
      setError(null)
      
      const res = await billingService.syncIndividualTier(tierId)
      if (!res.success) {
        throw new Error(res.error || 'Failed to sync tier')
      }
      
      // Reload data after sync
      await load()
      await loadSubscriptionCounts()
      
      // Force refresh edit form if dialog is open
      if (editOpen && editTier) {
        const updated = tiers.find(t => t.id === editTier.id)
        if (updated) {
          setEditForm({
            name: updated.name,
            tier_key: updated.tier_key,
            description: updated.description,
            base_price: updated.base_price as any,
            price_per_user: updated.price_per_user as any,
            free_users_limit: updated.free_users_limit as any,
            max_users: updated.max_users as any,
            max_rooms: updated.max_rooms as any,
            color: updated.color || defaultTierColor,
            is_active: updated.is_active,
            features: updated.features || {},
            requires_sales_contact: (updated as any).requires_sales_contact || false,
            sales_contact_message: (updated as any).sales_contact_message || ''
          })
          setEditTier(updated)
        }
      }
      
    } catch (error: any) {
      setError(`Failed to sync ${tierName}: ${error.message}`)
    } finally {
      setSyncingTier(null)
    }
  }



  const handleCreate = async () => {
    try {
      setCreating(true)
      setError(null)
      if (!form.name || !form.tier_key || !form.description) {
        setError('Name, tier_key, and description are required')
        return
      }
      // Coerce numeric fields at submit time only
      const payload = {
        ...form,
        base_price: Number(form.base_price ?? 0),
        price_per_user: Number(form.price_per_user ?? 0),
        free_users_limit: Number(form.free_users_limit ?? 0),
        max_users: Number(form.max_users ?? 0),
        max_rooms: Number(form.max_rooms ?? 0),
      } as any
      const result = await billingService.createBillingTier(payload)
      if (!result.success) {
        setError(result.error || result.data?.error || 'Failed to create tier')
        return
      }
      setForm({
        name: '', tier_key: '', description: '', base_price: undefined as any, price_per_user: undefined as any,
        free_users_limit: undefined as any, max_users: undefined as any, max_rooms: undefined as any, features: {}, 
        is_active: true, requires_sales_contact: false, sales_contact_message: ''
      })
      await load()
    } catch (e:any) {
      setError(e?.message || 'Failed to create tier')
    } finally {
      setCreating(false)
    }
  }

  const handleInitializeDefaults = async () => {
    try {
      setCreating(true)
      await billingService.initializeBillingTiers()
      await load()
    } catch (e:any) {
      setError(e?.message || 'Failed to initialize default tiers')
    } finally {
      setCreating(false)
    }
  }

  const loadPermissions = async (tierId: string) => {
    try {
      setLoadingPerms(true)
      if (!permDefs) {
        const defsRes = await permissionService.getDefinitions()
        if (defsRes.success && defsRes.definitions) {
          setPermDefs(defsRes.definitions)
        }
      }
      const permsRes = await permissionService.getTierPermissions(tierId)
      if (permsRes.success && permsRes.permissions) {
        setTierPerms(permsRes.permissions)
      } else {
        setTierPerms({})
      }
    } catch (e) {
      console.error('Error loading permissions:', e)
    } finally {
      setLoadingPerms(false)
    }
  }

  const openEdit = (tier: BillingTier) => {
    // Always get the latest tier data from the current tiers list
    const latestTier = tiers.find(t => t.id === tier.id) || tier
    
    console.log('openEdit - tier passed:', tier)
    console.log('openEdit - latestTier found:', latestTier)
    console.log('openEdit - requires_sales_contact:', (latestTier as any).requires_sales_contact)
    console.log('openEdit - sales_contact_message:', (latestTier as any).sales_contact_message)
    
    setEditTier(latestTier)
    setEditForm({
      name: latestTier.name,
      tier_key: latestTier.tier_key,
      description: latestTier.description,
      base_price: latestTier.base_price as any,
      price_per_user: latestTier.price_per_user as any,
      free_users_limit: latestTier.free_users_limit as any,
      max_users: latestTier.max_users as any,
      max_rooms: latestTier.max_rooms as any,
      color: latestTier.color || defaultTierColor,
      is_active: latestTier.is_active,
      features: latestTier.features || {},
      is_default: (latestTier as any).is_default || false,
      requires_sales_contact: (latestTier as any).requires_sales_contact || false,
      sales_contact_message: (latestTier as any).sales_contact_message || '',
    })
    setEditTab(0)
    // Load permissions for this tier
    loadPermissions(latestTier.id)
    setEditOpen(true)
  }

  const closeEdit = () => {
    setEditOpen(false)
    setEditTier(null)
    setEditForm({})
    setTierPerms({})
  }

  // Sync edit form with latest tier data when tiers are updated
  useEffect(() => {
    if (!editOpen || !editTier) return
    
    const updated = tiers.find(t => t.id === editTier.id)
    if (!updated) return
    
    console.log('Syncing edit form with latest data:', {
      currentForm: editForm,
      updatedTier: updated,
      requiresSalesContact: (updated as any).requires_sales_contact
    })
    
    // Create a new form object to force React to re-render
    const newFormData = {
      name: updated.name,
      tier_key: updated.tier_key,
      description: updated.description,
      base_price: updated.base_price as any,
      price_per_user: updated.price_per_user as any,
      free_users_limit: updated.free_users_limit as any,
      max_users: updated.max_users as any,
      max_rooms: updated.max_rooms as any,
      color: updated.color || defaultTierColor,
      is_active: updated.is_active,
      features: updated.features || {},
      is_default: (updated as any).is_default || false,
      requires_sales_contact: (updated as any).requires_sales_contact || false,
      sales_contact_message: (updated as any).sales_contact_message || ''
    }
    
    // Force update both the form and tier reference
    setEditForm(newFormData)
    setEditTier(updated)
    setFormRefreshKey(prev => prev + 1)
  }, [tiers, editOpen, editTier?.id])

  // Function to manually refresh the edit form with latest data
  const refreshEditForm = () => {
    if (!editTier) return
    
    const updated = tiers.find(t => t.id === editTier.id)
    if (!updated) return
    
    console.log('Manually refreshing edit form:', updated)
    
    setEditForm({
      name: updated.name,
      tier_key: updated.tier_key,
      description: updated.description,
      base_price: updated.base_price as any,
      price_per_user: updated.price_per_user as any,
      free_users_limit: updated.free_users_limit as any,
      max_users: updated.max_users as any,
      max_rooms: updated.max_rooms as any,
      color: updated.color || defaultTierColor,
      is_active: updated.is_active,
      features: updated.features || {},
      is_default: (updated as any).is_default || false,
      requires_sales_contact: (updated as any).requires_sales_contact || false,
      sales_contact_message: (updated as any).sales_contact_message || ''
    })
    setEditTier(updated)
    setFormRefreshKey(prev => prev + 1)
  }

  const saveEdit = async () => {
    if (!editTier) return
    try {
      setSaving(true)
      setError(null)
      
      if (!editForm.name || !editForm.tier_key || !editForm.description) {
        setError('Name, tier_key, and description are required')
        return
      }
      
      // Helper function to safely convert to number
      const toNumber = (value: any): number => {
        if (value === '' || value === null || value === undefined) return 0
        return Number(value)
      }
      
      const payload = {
        ...editForm,
        base_price: toNumber(editForm.base_price),
        price_per_user: toNumber(editForm.price_per_user),
        free_users_limit: toNumber(editForm.free_users_limit),
        max_users: toNumber(editForm.max_users),
        max_rooms: toNumber(editForm.max_rooms),
        is_default: editForm.is_default || false,
        requires_sales_contact: editForm.requires_sales_contact || false,
        sales_contact_message: editForm.sales_contact_message || '',
      } as any
      const result = await billingService.updateBillingTier(editTier.id, payload)
      if (!result.success) {
        setError(result.error || result.data?.error || 'Failed to update tier')
        return
      }
      // Persist permissions as a separate call
      if (Object.keys(tierPerms).length > 0) {
        await permissionService.updateTierPermissions(editTier.id, tierPerms)
      }
      await load()
      
      // Update edit form with latest data before closing
      const updated = tiers.find(t => t.id === editTier.id)
      if (updated) {
        setEditForm({
          name: updated.name,
          tier_key: updated.tier_key,
          description: updated.description,
          base_price: updated.base_price as any,
          price_per_user: updated.price_per_user as any,
          free_users_limit: updated.free_users_limit as any,
          max_users: updated.max_users as any,
          max_rooms: updated.max_rooms as any,
          color: updated.color || defaultTierColor,
          is_active: updated.is_active,
          features: updated.features || {},
          requires_sales_contact: (updated as any).requires_sales_contact || false,
          sales_contact_message: (updated as any).sales_contact_message || ''
        })
        setEditTier(updated)
      }
      
      closeEdit()
    } catch (e:any) {
      setError(e?.message || 'Failed to update tier')
    } finally {
      setSaving(false)
    }
  }

  const handleMigration = async (dryRun: boolean = true) => {
    try {
      setMigrating(true)
      setError(null)
      const result = await billingService.migrateCompanyBillingTiers(dryRun)
      if (result.success) {
        console.log('Migration result:', result.results)
        await loadMigrationStatus()
        if (!dryRun) {
          setError(`Migration completed: ${result.results.migrated} migrated, ${result.results.skipped} skipped, ${result.results.errors} errors`)
        } else {
          setError(`Dry run completed: Would migrate ${result.results.migrated} companies, skip ${result.results.skipped}, ${result.results.errors} errors`)
        }
      }
    } catch (e:any) {
      setError(e?.message || 'Failed to migrate companies')
    } finally {
      setMigrating(false)
    }
  }

  // Open delete dialog for a tier
  const openDeleteDialog = async (tier: BillingTier) => {
    setTierToDelete(tier)
    setReplacementTierId('')
    setAffectedCompaniesInfo(null)
    setDeleteDialogOpen(true)
    
    // Load affected companies info
    setLoadingAffectedCompanies(true)
    try {
      const result = await billingService.getAffectedCompaniesForTier(tier.id)
      if (result.success) {
        setAffectedCompaniesInfo({
          tier_id: result.tier_id,
          tier_key: result.tier_key,
          tier_name: result.tier_name,
          affected_count: result.affected_count,
          affected_companies: result.affected_companies,
          can_delete_safely: result.can_delete_safely
        })
      } else {
        setError(result.error || 'Failed to load affected companies')
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to load affected companies')
    } finally {
      setLoadingAffectedCompanies(false)
    }
  }

  // Close delete dialog
  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false)
    setTierToDelete(null)
    setReplacementTierId('')
    setAffectedCompaniesInfo(null)
  }

  // Handle delete confirmation
  const handleDeleteTier = async () => {
    if (!tierToDelete) return
    
    const affectedCount = affectedCompaniesInfo?.affected_count?.total || 0
    
    // Require replacement tier if there are affected companies
    if (affectedCount > 0 && !replacementTierId) {
      setError('Please select a replacement tier for affected companies')
      return
    }
    
    try {
      setDeleting(true)
      setError(null)
      
      const result = await billingService.deleteBillingTierWithReassignment(
        tierToDelete.id,
        affectedCount > 0 ? replacementTierId : null,
        userData?.id
      )
      
      if (result.success) {
        closeDeleteDialog()
        // Force a fresh reload to ensure deleted tier is removed from the list
        setTiers([]) // Clear current tiers to force refresh
        await load()
        await loadSubscriptionCounts()
        
        // Show success message
        const message = result.audit?.affected_companies && result.audit.affected_companies > 0
          ? `Tier "${tierToDelete.name}" deleted. ${result.audit.affected_companies} companies moved to the replacement tier. ${result.audit.email_notifications?.successful || 0} notification emails sent.`
          : `Tier "${tierToDelete.name}" deleted successfully.`
        setError(message) // Using error state for success message temporarily
      } else {
        setError(result.error || 'Failed to delete tier')
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to delete tier')
    } finally {
      setDeleting(false)
    }
  }

  // Get available replacement tiers (active tiers excluding the one being deleted)
  const getAvailableReplacementTiers = () => {
    if (!tierToDelete) return tiers.filter(t => t.is_active)
    return tiers.filter(t => t.is_active && t.id !== tierToDelete.id)
  }

  return (
    <Box sx={billingSx.adminBillingPageRootSx(theme)}>
      {/* Header aligned with SEVV theme */}
      <Box sx={billingSx.adminBillingHeaderRowSx(theme)}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <Box sx={billingSx.adminBillingPrimaryIconBadgeSx(theme, 3)}>
            <MonetizationOnIcon sx={{ fontSize: '1.5rem' }} />
          </Box>
          <Box>
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 600, 
                color: 'text.primary',
                fontSize: '1.875rem',
                lineHeight: '1.25',
                fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
                letterSpacing: '-0.025em',
                marginBottom: '0.25rem'
              }}
            >
              Billing Settings
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'text.secondary',
                fontSize: '1rem',
                lineHeight: '1.5',
                fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif'
              }}
            >
              Manage plans, pricing overrides, and tier configuration
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => onSectionChange('adminMenu')}
            sx={billingSx.adminBillingToolbarOutlinedButtonSx(theme)}
          >
            Back
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => {
              load()
              loadSubscriptionCounts()
              refreshBillingData()
            }}
            disabled={loading || loadingSubscriptions}
            sx={billingSx.adminBillingToolbarOutlinedButtonSx(theme)}
          >
            {loading || loadingSubscriptions ? <CircularProgress size={18} sx={{ color: 'primary.main' }} /> : 'Refresh'}
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<SettingsIcon />}
            onClick={handleInitializeDefaults}
            disabled={creating}
            sx={billingSx.adminBillingPrimaryCtaButtonSx(theme)}
          >
            {creating ? <CircularProgress size={18} color="inherit" /> : 'Init Default Tiers'}
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={billingSx.adminBillingErrorAlertSx(theme)}>
          {error}
        </Alert>
      )}

      {/* Current Company Billing Status */}
      {currentCompany && (
        <Card sx={{
          marginBottom: '1.5rem',
          bgcolor: 'background.paper',
                    backgroundImage: 'none',
          border: '1px solid',
                    borderColor: 'divider',
          borderRadius: '1rem',
          boxShadow: theme.shadows[2],
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-0.125rem)',
            boxShadow: theme.shadows[6],
            borderColor: 'primary.main'
          }
        }}>
          <CardContent sx={{ padding: '1.5rem' }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '1rem', 
              marginBottom: '1.5rem' 
            }}>
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: alpha(theme.palette.primary.main, 0.12),
                    backgroundImage: 'none',
                color: 'primary.main',
                borderRadius: '0.5rem',
                width: '2.5rem',
                height: '2.5rem',
                border: `1px solid ${alpha(theme.palette.primary.main, 0.28)}`
              }}>
                <AccountBalance sx={{ fontSize: '1.25rem' }} />
              </Box>
              <Typography 
                variant="h6" 
                sx={{ 
                  color: 'text.primary', 
                  fontWeight: 600,
                  fontSize: '1.125rem',
                  lineHeight: '1.25',
                  fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
                  letterSpacing: '-0.01em'
                }}
              >
                Current Company Billing Status
              </Typography>
              {loadingBillingData && <CircularProgress size={20} sx={{ color: 'primary.main' }} />}
            </Box>
            
            {billingDataError && (
              <Alert 
                severity="error" 
                sx={{ 
                  marginBottom: '1rem',
                  bgcolor: alpha(theme.palette.error.main, 0.08),
                  backgroundImage: 'none',
                  border: '1px solid',
                  borderColor: alpha(theme.palette.error.main, 0.35),
                  borderRadius: '0.75rem',
                  '& .MuiAlert-message': {
                    color: 'error.main',
                    fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
                    fontSize: '0.875rem',
                    fontWeight: 500
                  }
                }}
              >
                {billingDataError}
              </Alert>
            )}
            
            {currentCompany && (
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Box sx={{ 
                    padding: '1rem', 
                    bgcolor: 'action.hover',
                    backgroundImage: 'none', 
                    borderRadius: '0.75rem',
                    border: '1px solid',
                    borderColor: 'divider'
                  }}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: 'text.secondary', 
                        marginBottom: '0.5rem',
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif'
                      }}
                    >
                      Company
                    </Typography>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        color: 'text.primary', 
                        fontWeight: 600,
                        fontSize: '1rem',
                        lineHeight: '1.25',
                        fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif'
                      }}
                    >
                      {currentCompany.name}
                    </Typography>
                  </Box>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Box sx={{ 
                    padding: '1rem', 
                    bgcolor: 'action.hover',
                    backgroundImage: 'none', 
                    borderRadius: '0.75rem',
                    border: '1px solid',
                    borderColor: 'divider'
                  }}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: 'text.secondary', 
                        marginBottom: '0.5rem',
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif'
                      }}
                    >
                      Current Plan
                    </Typography>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        color: 'text.primary', 
                        fontWeight: 600,
                        fontSize: '1rem',
                        lineHeight: '1.25',
                        fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif'
                      }}
                    >
                      {billingAccount?.billing_tier?.name || 'No Plan Selected'}
                    </Typography>
                  </Box>
                </Grid>
                
                {billingCalculation && (
                  <>
                    <Grid item xs={12} md={4}>
                      <Box sx={{ 
                        padding: '1rem', 
                        bgcolor: 'action.hover',
                    backgroundImage: 'none', 
                        borderRadius: '0.75rem',
                        border: '1px solid',
                    borderColor: 'divider'
                      }}>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: 'text.secondary', 
                            marginBottom: '0.5rem',
                            fontSize: '0.875rem',
                            fontWeight: 500,
                            fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif'
                          }}
                        >
                          Active Users
                        </Typography>
                        <Typography 
                          variant="h6" 
                          sx={{ 
                            color: 'text.primary', 
                            fontWeight: 600,
                            fontSize: '1rem',
                            lineHeight: '1.25',
                            fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif'
                          }}
                        >
                          {billingCalculation.active_users}
                        </Typography>
                      </Box>
                    </Grid>
                    
                    <Grid item xs={12} md={4}>
                      <Box sx={{ 
                        padding: '1rem', 
                        bgcolor: 'action.hover',
                    backgroundImage: 'none', 
                        borderRadius: '0.75rem',
                        border: '1px solid',
                    borderColor: 'divider'
                      }}>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: 'text.secondary', 
                            marginBottom: '0.5rem',
                            fontSize: '0.875rem',
                            fontWeight: 500,
                            fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif'
                          }}
                        >
                          Monthly Cost
                        </Typography>
                        <Typography 
                          variant="h6" 
                          sx={{ 
                            color: 'success.main', 
                            fontWeight: 600,
                            fontSize: '1rem',
                            lineHeight: '1.25',
                            fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif'
                          }}
                        >
                          {billingService.formatCurrency(billingCalculation.total_cost)}
                        </Typography>
                      </Box>
                    </Grid>
                    
                    <Grid item xs={12} md={4}>
                      <Box sx={{ 
                        padding: '1rem', 
                        bgcolor: 'action.hover',
                    backgroundImage: 'none', 
                        borderRadius: '0.75rem',
                        border: '1px solid',
                    borderColor: 'divider'
                      }}>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: 'text.secondary', 
                            marginBottom: '0.5rem',
                            fontSize: '0.875rem',
                            fontWeight: 500,
                            fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif'
                          }}
                        >
                          Billing Cycle
                        </Typography>
                        <Typography 
                          variant="h6" 
                          sx={{ 
                            color: 'text.primary', 
                            fontWeight: 600,
                            fontSize: '1rem',
                            lineHeight: '1.25',
                            fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif'
                          }}
                        >
                          {billingAccount?.billing_cycle || 'monthly'}
                        </Typography>
                      </Box>
                    </Grid>
                  </>
                )}
              </Grid>
            )}
          </CardContent>
        </Card>
      )}



      {/* Migration Status Section */}
      {migrationStatus && (
        <Card sx={{
          marginBottom: '1.5rem',
          bgcolor: 'background.paper',
                    backgroundImage: 'none',
          border: '1px solid',
                    borderColor: 'divider',
          borderRadius: '1rem',
          boxShadow: theme.shadows[2],
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-0.125rem)',
            boxShadow: theme.shadows[6],
            borderColor: 'primary.main'
          }
        }}>
          <CardContent sx={{ padding: '1.5rem' }}>
            <Typography 
              variant="h6" 
              sx={{ 
                marginBottom: '1.5rem', 
                color: 'text.primary', 
                fontWeight: 600,
                fontSize: '1.125rem',
                lineHeight: '1.25',
                fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
                letterSpacing: '-0.01em'
              }}
            >
              Migration Status
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <Box sx={{ 
                  padding: '1rem', 
                  bgcolor: 'action.hover',
                    backgroundImage: 'none', 
                  borderRadius: '0.75rem',
                  border: '1px solid',
                    borderColor: 'divider'
                }}>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: 'text.secondary',
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
                      marginBottom: '0.5rem'
                    }}
                  >
                    Total Companies:
                  </Typography>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      color: 'text.primary',
                      fontWeight: 600,
                      fontSize: '1.25rem',
                      lineHeight: '1.25',
                      fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif'
                    }}
                  >
                    {migrationStatus.total_companies}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={3}>
                <Box sx={{ 
                  padding: '1rem', 
                  bgcolor: alpha(theme.palette.warning.main, 0.15),
                  backgroundImage: 'none',
                  borderRadius: '0.75rem',
                  border: `1px solid ${alpha(theme.palette.warning.main, 0.45)}`
                }}>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: 'warning.dark',
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
                      marginBottom: '0.5rem'
                    }}
                  >
                    Old System:
                  </Typography>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      color: 'warning.dark',
                      fontWeight: 600,
                      fontSize: '1.25rem',
                      lineHeight: '1.25',
                      fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif'
                    }}
                  >
                    {migrationStatus.old_system_companies}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={3}>
                <Box sx={{ 
                  padding: '1rem', 
                  bgcolor: alpha(theme.palette.success.main, 0.12),
                  backgroundImage: 'none',
                  borderRadius: '0.75rem',
                  border: `1px solid ${alpha(theme.palette.success.main, 0.45)}`
                }}>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: 'success.dark',
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
                      marginBottom: '0.5rem'
                    }}
                  >
                    New System:
                  </Typography>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      color: 'success.dark',
                      fontWeight: 600,
                      fontSize: '1.25rem',
                      lineHeight: '1.25',
                      fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif'
                    }}
                  >
                    {migrationStatus.new_system_companies}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={3}>
                <Box sx={{ 
                  padding: '1rem', 
                  bgcolor: 'action.hover',
                    backgroundImage: 'none', 
                  borderRadius: '0.75rem',
                  border: '1px solid',
                    borderColor: 'divider'
                }}>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: 'text.secondary',
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
                      marginBottom: '0.5rem'
                    }}
                  >
                    Progress:
                  </Typography>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      color: 'text.primary',
                      fontWeight: 600,
                      fontSize: '1.25rem',
                      lineHeight: '1.25',
                      fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif'
                    }}
                  >
                    {migrationStatus.migration_progress}%
                  </Typography>
                </Box>
              </Grid>
            </Grid>
            {migrationStatus.needs_migration && (
              <Box sx={{ 
                marginTop: '1.5rem', 
                display: 'flex', 
                gap: '0.75rem',
                paddingTop: '1.5rem',
                borderTop: '1px solid',
                    borderTopColor: 'divider'
              }}>
                <Button 
                  variant="outlined" 
                  onClick={() => handleMigration(true)} 
                  disabled={migrating}
                  sx={billingSx.adminBillingToolbarOutlinedButtonSx(theme)}
                >
                  {migrating ? <CircularProgress size={18} sx={{ color: 'primary.main' }} /> : 'Test Migration (Dry Run)'}
                </Button>
                <Button 
                  variant="contained"
                  color="warning"
                  onClick={() => handleMigration(false)} 
                  disabled={migrating}
                  sx={billingSx.adminBillingWarningCtaButtonSx(theme)}
                >
                  {migrating ? <CircularProgress size={18} color="inherit" /> : 'Run Migration'}
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={5}>
          <Card sx={{
            bgcolor: 'background.paper',
                    backgroundImage: 'none',
            border: '1px solid',
                    borderColor: 'divider',
            borderRadius: '1rem',
            boxShadow: theme.shadows[2],
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-0.125rem)',
              boxShadow: theme.shadows[6],
              borderColor: 'primary.main'
            }
          }}>
            <CardContent sx={{ padding: '1.5rem' }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  marginBottom: '1.5rem', 
                  color: 'text.primary', 
                  fontWeight: 600,
                  fontSize: '1.125rem',
                  lineHeight: '1.25',
                  fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
                  letterSpacing: '-0.01em'
                }}
              >
                Create Tier
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField 
                    label="Name" 
                    fullWidth 
                    value={form.name || ''} 
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    sx={{
                      '& .MuiInputBase-input': { 
                        color: 'text.primary',
                        fontSize: '0.875rem',
                        fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif'
                      },
                      '& .MuiInputLabel-root': { 
                        color: 'text.secondary',
                        fontSize: '0.875rem',
                        fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif'
                      },
                      '& .MuiOutlinedInput-notchedOutline': { 
                        borderColor: 'divider' 
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': { 
                        borderColor: 'primary.main' 
                      },
                      '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'primary.main'
                      }
                    }} 
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField 
                    label="Tier Key" 
                    fullWidth 
                    value={form.tier_key || ''} 
                    onChange={(e) => setForm({ ...form, tier_key: e.target.value })}
                    sx={{
                      '& .MuiInputBase-input': { 
                        color: 'text.primary',
                        fontSize: '0.875rem',
                        fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif'
                      },
                      '& .MuiInputLabel-root': { 
                        color: 'text.secondary',
                        fontSize: '0.875rem',
                        fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif'
                      },
                      '& .MuiOutlinedInput-notchedOutline': { 
                        borderColor: 'divider' 
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': { 
                        borderColor: 'primary.main' 
                      },
                      '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'primary.main'
                      }
                    }} 
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField 
                    label="Description" 
                    fullWidth 
                    required
                    value={form.description || ''} 
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    error={!form.description}
                    helperText={!form.description ? "Description is required for Stripe product creation" : ""}
                    sx={{
                      '& .MuiInputBase-input': { 
                        color: 'text.primary',
                        fontSize: '0.875rem',
                        fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif'
                      },
                      '& .MuiInputLabel-root': { 
                        color: 'text.secondary',
                        fontSize: '0.875rem',
                        fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif'
                      },
                      '& .MuiOutlinedInput-notchedOutline': { 
                        borderColor: 'divider' 
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': { 
                        borderColor: 'primary.main' 
                      },
                      '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'primary.main'
                      },
                      '& .MuiFormHelperText-root': { 
                        color: 'text.secondary',
                        fontSize: '0.75rem',
                        fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif'
                      }
                    }} 
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '1rem',
                    padding: '1rem',
                    bgcolor: 'action.hover',
                    backgroundImage: 'none',
                    borderRadius: '0.75rem',
                    border: '1px solid',
                    borderColor: 'divider'
                  }}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: 'text.secondary',
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif'
                      }}
                    >
                      Tier Color:
                    </Typography>
                    <input
                      type="color"
                      value={form.color || defaultTierColor}
                      onChange={(e) => setForm({ ...form, color: e.target.value })}
                      style={{ 
                        width: '2.5rem', 
                        height: '2.5rem', 
                        border: '2px solid',
                    borderColor: 'divider', 
                        borderRadius: '0.5rem',
                        cursor: 'pointer',
                        background: 'transparent',
                        transition: 'border-color 0.2s ease'
                      }}
                      onMouseEnter={(e) => (e.target as HTMLInputElement).style.borderColor = theme.palette.primary.main}
                      onMouseLeave={(e) => (e.target as HTMLInputElement).style.borderColor = theme.palette.divider}
                      aria-label="Pick tier color"
                    />
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: 'text.primary',
                        fontFamily: 'monospace',
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        bgcolor: 'action.selected',
                    backgroundImage: 'none',
                        padding: '0.25rem 0.5rem',
                        borderRadius: '0.375rem'
                      }}
                    >
                      {form.color || defaultTierColor}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <TextField 
                    type="number" 
                    label="Base Price" 
                    fullWidth 
                    value={form.base_price ?? ''} 
                    onChange={(e) => setForm({ ...form, base_price: e.target.value === '' ? '' as any : e.target.value })}
                    sx={{
                      '& .MuiInputBase-input': { 
                        color: 'text.primary',
                        fontSize: '0.875rem',
                        fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif'
                      },
                      '& .MuiInputLabel-root': { 
                        color: 'text.secondary',
                        fontSize: '0.875rem',
                        fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif'
                      },
                      '& .MuiOutlinedInput-notchedOutline': { 
                        borderColor: 'divider' 
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': { 
                        borderColor: 'primary.main' 
                      },
                      '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'primary.main'
                      }
                    }} 
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField 
                    type="number" 
                    label="Price per User" 
                    fullWidth 
                    value={form.price_per_user ?? ''} 
                    onChange={(e) => setForm({ ...form, price_per_user: e.target.value === '' ? '' as any : e.target.value })}
                    sx={{
                      '& .MuiInputBase-input': { 
                        color: 'text.primary',
                        fontSize: '0.875rem',
                        fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif'
                      },
                      '& .MuiInputLabel-root': { 
                        color: 'text.secondary',
                        fontSize: '0.875rem',
                        fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif'
                      },
                      '& .MuiOutlinedInput-notchedOutline': { 
                        borderColor: 'divider' 
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': { 
                        borderColor: 'primary.main' 
                      },
                      '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'primary.main'
                      }
                    }} 
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField 
                    type="number" 
                    label="Free Users" 
                    fullWidth 
                    value={form.free_users_limit ?? ''} 
                    onChange={(e) => setForm({ ...form, free_users_limit: e.target.value === '' ? '' as any : e.target.value })}
                    sx={{
                      '& .MuiInputBase-input': { 
                        color: 'text.primary',
                        fontSize: '0.875rem',
                        fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif'
                      },
                      '& .MuiInputLabel-root': { 
                        color: 'text.secondary',
                        fontSize: '0.875rem',
                        fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif'
                      },
                      '& .MuiOutlinedInput-notchedOutline': { 
                        borderColor: 'divider' 
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': { 
                        borderColor: 'primary.main' 
                      },
                      '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'primary.main'
                      }
                    }} 
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField 
                    type="number" 
                    label="Max Users" 
                    fullWidth 
                    value={form.max_users ?? ''} 
                    onChange={(e) => setForm({ ...form, max_users: e.target.value === '' ? '' as any : e.target.value })}
                    sx={{
                      '& .MuiInputBase-input': { 
                        color: 'text.primary',
                        fontSize: '0.875rem',
                        fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif'
                      },
                      '& .MuiInputLabel-root': { 
                        color: 'text.secondary',
                        fontSize: '0.875rem',
                        fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif'
                      },
                      '& .MuiOutlinedInput-notchedOutline': { 
                        borderColor: 'divider' 
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': { 
                        borderColor: 'primary.main' 
                      },
                      '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'primary.main'
                      }
                    }} 
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField 
                    type="number" 
                    label="Max Rooms" 
                    fullWidth 
                    value={form.max_rooms ?? ''} 
                    onChange={(e) => setForm({ ...form, max_rooms: e.target.value === '' ? '' as any : e.target.value })}
                    sx={{
                      '& .MuiInputBase-input': { 
                        color: 'text.primary',
                        fontSize: '0.875rem',
                        fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif'
                      },
                      '& .MuiInputLabel-root': { 
                        color: 'text.secondary',
                        fontSize: '0.875rem',
                        fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif'
                      },
                      '& .MuiOutlinedInput-notchedOutline': { 
                        borderColor: 'divider' 
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': { 
                        borderColor: 'primary.main' 
                      },
                      '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'primary.main'
                      }
                    }} 
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel 
                    control={
                      <Switch 
                        checked={!!form.is_default} 
                        onChange={(e) => setForm({ ...form, is_default: e.target.checked })}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: 'primary.main',
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            bgcolor: 'primary.main',
                            backgroundImage: 'none',
                          },
                        }}
                      />
                    }
                    label={
                      <Typography 
                        sx={{ 
                          color: 'text.primary',
                          fontSize: '0.875rem',
                          fontWeight: 500,
                          fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif'
                        }}
                      >
                        Default Tier for New Users
                      </Typography>
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel 
                    control={
                      <Switch 
                        checked={!!form.requires_sales_contact} 
                        onChange={(e) => setForm({ ...form, requires_sales_contact: e.target.checked })}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: 'primary.main',
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            bgcolor: 'primary.main',
                            backgroundImage: 'none',
                          },
                        }}
                      />
                    }
                    label={
                      <Typography 
                        sx={{ 
                          color: 'text.primary',
                          fontSize: '0.875rem',
                          fontWeight: 500,
                          fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif'
                        }}
                      >
                        Requires Sales Contact
                      </Typography>
                    }
                  />
                </Grid>
                {form.requires_sales_contact && (
                  <Grid item xs={12}>
                    <TextField 
                      label="Sales Contact Message" 
                      fullWidth 
                      multiline
                      rows={3}
                      value={form.sales_contact_message || ''} 
                      onChange={(e) => setForm({ ...form, sales_contact_message: e.target.value })}
                      placeholder="Enter custom message for sales contact requirement..."
                      sx={{
                        '& .MuiInputBase-input': { 
                          color: 'text.primary',
                          fontSize: '0.875rem',
                          fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif'
                        },
                        '& .MuiInputLabel-root': { 
                          color: 'text.secondary',
                          fontSize: '0.875rem',
                          fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif'
                        },
                        '& .MuiOutlinedInput-notchedOutline': { 
                          borderColor: 'divider' 
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': { 
                          borderColor: 'primary.main' 
                        },
                        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'primary.main'
                        }
                      }} 
                    />
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Button 
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />} 
                    onClick={handleCreate} 
                    disabled={creating}
                    sx={{
                      ...((billingSx.adminBillingPrimaryCtaButtonSx(theme) as object) || {}),
                      py: 0.75,
                      px: 3,
                    }}
                  >
                    {creating ? <CircularProgress size={18} color="inherit" /> : 'Create Tier'}
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={7}>
          <Card sx={{
            bgcolor: 'background.paper',
                    backgroundImage: 'none',
            border: '1px solid',
                    borderColor: 'divider',
            borderRadius: '1rem',
            boxShadow: theme.shadows[2],
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-0.125rem)',
              boxShadow: theme.shadows[6],
              borderColor: 'primary.main'
            }
          }}>
            <CardContent sx={{ padding: '1.5rem' }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  marginBottom: '1.5rem', 
                  color: 'text.primary', 
                  fontWeight: 600,
                  fontSize: '1.125rem',
                  lineHeight: '1.25',
                  fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
                  letterSpacing: '-0.01em'
                }}
              >
                Existing Tiers
              </Typography>
              {loading ? (
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center',
                  padding: '3rem'
                }}>
                  <CircularProgress sx={{ color: 'primary.main' }} />
                </Box>
              ) : (
                <Grid container spacing={2}>
                  {tiers.map((t) => (
                    <Grid item xs={12} key={t.id}>
                      <Card sx={{
                        bgcolor: 'background.paper',
                    backgroundImage: 'none',
                        border: '1px solid',
                    borderColor: 'divider',
                        borderRadius: '0.75rem',
                        boxShadow: theme.shadows[2],
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                          transform: 'translateY(-0.125rem)',
                          boxShadow: theme.shadows[6],
                          borderColor: 'primary.main'
                        }
                      }}>
                        <CardContent sx={{ padding: '1.25rem' }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <Box sx={{ flex: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
                                <Typography 
                                  variant="subtitle1" 
                                  fontWeight={600} 
                                  sx={{ 
                                    color: 'text.primary',
                                    fontSize: '1rem',
                                    lineHeight: '1.25',
                                    fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif'
                                  }}
                                >
                                  {t.name}
                                </Typography>
                                <Box sx={{ 
                                  width: '0.75rem', 
                                  height: '0.75rem', 
                                  borderRadius: '50%', 
                                  bgcolor: t.color || theme.palette.text.secondary, 
                                  border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
                                }} />
                              </Box>
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  color: 'text.secondary', 
                                  marginBottom: '0.75rem',
                                  fontSize: '0.875rem',
                                  lineHeight: '1.5',
                                  fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif'
                                }}
                              >
                                {t.description}
                              </Typography>
                              
                              {/* Pricing Info */}
                              <Box sx={{ 
                                display: 'flex', 
                                gap: '1rem', 
                                marginBottom: '1rem', 
                                flexWrap: 'wrap' 
                              }}>
                                {t.requires_sales_contact ? (
                                  <Typography 
                                    variant="body2" 
                                    component="span"
                                    sx={billingSx.adminBillingSalesContactChipSx(theme)}
                                  >
                                    Contact Sales for Pricing
                                  </Typography>
                                ) : (
                                  <>
                                    <Typography 
                                      variant="body2" 
                                      sx={{ 
                                        color: 'text.primary',
                                        fontSize: '0.875rem',
                                        fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
                                        bgcolor: 'action.selected',
                    backgroundImage: 'none',
                                        padding: '0.25rem 0.5rem',
                                        borderRadius: '0.375rem'
                                      }}
                                    >
                                      Base: ${t.base_price}/mo
                                    </Typography>
                                    <Typography 
                                      variant="body2" 
                                      sx={{ 
                                        color: 'text.primary',
                                        fontSize: '0.875rem',
                                        fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
                                        bgcolor: 'action.selected',
                    backgroundImage: 'none',
                                        padding: '0.25rem 0.5rem',
                                        borderRadius: '0.375rem'
                                      }}
                                    >
                                      Per User: ${t.price_per_user}/mo
                                    </Typography>
                                    <Typography 
                                      variant="body2" 
                                      sx={{ 
                                        color: 'text.primary',
                                        fontSize: '0.875rem',
                                        fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
                                        bgcolor: 'action.selected',
                    backgroundImage: 'none',
                                        padding: '0.25rem 0.5rem',
                                        borderRadius: '0.375rem'
                                      }}
                                    >
                                      Free: {t.free_users_limit}
                                    </Typography>
                                    <Typography 
                                      variant="body2" 
                                      sx={{ 
                                        color: 'text.primary',
                                        fontSize: '0.875rem',
                                        fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
                                        bgcolor: 'action.selected',
                    backgroundImage: 'none',
                                        padding: '0.25rem 0.5rem',
                                        borderRadius: '0.375rem'
                                      }}
                                    >
                                      Max: {t.max_users === -1 ? 'Unlimited' : t.max_users}
                                    </Typography>
                                  </>
                                )}
                              </Box>

                              {/* Subscription Stats & Sync Status */}
                              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                                <Chip 
                                  label={t.is_active ? 'Active' : 'Inactive'} 
                                  color={t.is_active ? 'success' as any : 'default'} 
                                  size="small"
                                />
                                {(t as any).is_default && (
                                  <Chip 
                                    label="Default"
                                    color="info"
                                    size="small"
                                  />
                                )}
                                {t.requires_sales_contact && (
                                  <Chip 
                                    label="Sales Contact" 
                                    color="warning" 
                                    size="small"
                                  />
                                )}
                                
                                {/* Sync Status */}
                                {subscriptionCounts[t.tier_key] && (
                                  <>
                                    <Chip 
                                      label={subscriptionCounts[t.tier_key].is_synced ? 'Synced' : 'Not Synced'} 
                                      color={subscriptionCounts[t.tier_key].is_synced ? 'primary' : 'warning'} 
                                      size="small"
                                    />
                                    
                                    {/* Subscription Count */}
                                    {subscriptionCounts[t.tier_key].is_synced && (
                                      <Chip 
                                        label={`${subscriptionCounts[t.tier_key].active_subscriptions} active subs`} 
                                        color="success"
                                        size="small"
                                      />
                                    )}
                                  </>
                                )}
                                
                                {loadingSubscriptions && (
                                  <CircularProgress size={16} sx={{ color: 'text.secondary' }} />
                                )}
                              </Box>
                            </Box>
                            
                            {/* Action Buttons */}
                            <Box sx={{ 
                              display: 'flex', 
                              flexDirection: 'column', 
                              gap: '0.5rem', 
                              minWidth: '6.25rem' 
                            }}>
                              <Button 
                                size="small" 
                                variant="outlined" 
                                startIcon={syncingTier === t.id ? <CircularProgress size={12} sx={{ color: 'primary.main' }} /> : <SyncIcon sx={{ fontSize: 14 }} />}
                                onClick={() => syncIndividualTier(t.id, t.name)}
                                disabled={syncingTier === t.id}
                                sx={{
                                  minWidth: '4.375rem',
                                  height: '2rem',
                                  fontSize: '0.75rem',
                                  borderColor: 'divider',
                                  color: 'text.secondary',
                                  fontWeight: 500,
                                  textTransform: 'none',
                                  fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
                                  '&:hover': {
                                    borderColor: 'primary.main',
                                    color: 'primary.main',
                                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                    backgroundImage: 'none',
                                  },
                                  '&:disabled': {
                                    color: 'text.disabled',
                                    borderColor: 'divider',
                                    bgcolor: 'action.hover',
                    backgroundImage: 'none'
                                  }
                                }}
                              >
                                {syncingTier === t.id ? '' : 'Sync'}
                              </Button>
                              <Button 
                                size="small" 
                                variant="outlined" 
                                startIcon={<EditIcon sx={{ fontSize: 14 }} />} 
                                onClick={() => openEdit(t)}
                                sx={{
                                  minWidth: '4.375rem',
                                  height: '2rem',
                                  fontSize: '0.75rem',
                                  borderColor: 'divider',
                                  color: 'text.secondary',
                                  fontWeight: 500,
                                  textTransform: 'none',
                                  fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
                                  '&:hover': {
                                    borderColor: 'primary.main',
                                    color: 'primary.main',
                                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                    backgroundImage: 'none',
                                  }
                                }}
                              >
                                Edit
                              </Button>
                              <Button 
                                size="small" 
                                variant="outlined" 
                                startIcon={<DeleteIcon sx={{ fontSize: 14 }} />} 
                                onClick={() => openDeleteDialog(t)}
                                disabled={(t as any).is_default}
                                sx={{
                                  minWidth: '4.375rem',
                                  height: '2rem',
                                  fontSize: '0.75rem',
                                  borderColor: alpha(theme.palette.error.main, 0.35),
                                  color: 'error.main',
                                  fontWeight: 500,
                                  textTransform: 'none',
                                  fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
                                  '&:hover': {
                                    borderColor: 'error.main',
                                    color: 'error.main',
                                    bgcolor: alpha(theme.palette.error.main, 0.08),
                                    backgroundImage: 'none',
                                  },
                                  '&:disabled': {
                                    color: 'text.disabled',
                                    borderColor: 'divider',
                                    bgcolor: 'action.hover',
                                    backgroundImage: 'none'
                                  }
                                }}
                              >
                                Delete
                              </Button>
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Edit Tier Dialog */}
      <Dialog 
        key={editTier?.id || 'no-tier'} 
        open={editOpen} 
        onClose={closeEdit} 
        maxWidth="lg" 
        fullWidth
        PaperProps={{ 
          sx: { 
            bgcolor: 'background.paper',
                    backgroundImage: 'none', 
            border: '1px solid',
                    borderColor: 'divider', 
            borderRadius: '1rem', 
            boxShadow: theme.shadows[10],
            overflow: 'hidden'
          } 
        }}>
        <DialogTitle sx={{ 
          color: 'text.primary', 
          fontWeight: 600, 
          borderBottom: `1px solid ${theme.palette.divider}`, 
          bgcolor: 'action.hover',
                    backgroundImage: 'none',
          padding: '1.5rem 1.5rem 1rem 1.5rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontSize: '1.25rem',
          lineHeight: '1.25',
          fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
          letterSpacing: '-0.01em'
        }}>
          Edit Billing Tier
          <Button
            size="small"
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={refreshEditForm}
            sx={{
              color: 'text.secondary',
              borderColor: 'divider',
              fontWeight: 500,
              fontSize: '0.875rem',
              padding: '0.5rem 1rem',
              borderRadius: '0.5rem',
              textTransform: 'none',
              fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
              '&:hover': {
                borderColor: 'primary.main',
                color: 'primary.main',
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                    backgroundImage: 'none',
              },
            }}
          >
            Refresh Data
          </Button>
        </DialogTitle>
        <Tabs 
          value={editTab} 
          onChange={(_, v) => setEditTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            ...SEVV_TABS_OVERFLOW_SX,
            borderBottom: `1px solid ${theme.palette.divider}`,
            bgcolor: 'background.paper',
                    backgroundImage: 'none',
            padding: '0.25rem 1.5rem',
            '& .MuiTab-root': { 
              color: 'text.secondary',
              fontWeight: 500,
              fontSize: '0.875rem',
              textTransform: 'none',
              fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
              minHeight: '2.5rem',
              padding: '0.5rem 1rem',
              borderRadius: '0.5rem',
              marginRight: '0.5rem',
              transition: 'all 0.2s ease',
              '&:hover': {
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                    backgroundImage: 'none',
                color: 'primary.main'
              }
            },
            '& .MuiTab-root.Mui-selected': { 
              color: 'primary.main',
              fontWeight: 600,
              bgcolor: alpha(theme.palette.primary.main, 0.12),
                    backgroundImage: 'none',
              border: `1px solid ${alpha(theme.palette.primary.main, 0.28)}`,
              '&:hover': {
                bgcolor: alpha(theme.palette.primary.main, 0.18),
                backgroundImage: 'none',
              }
            },
            '& .MuiTabs-indicator': { 
              bgcolor: 'primary.main',
              height: '3px',
              borderRadius: '3px 3px 0 0'
            },
          }}
        >
          <Tab label="General" />
          <Tab label="Permissions" />
        </Tabs>
        <DialogContent sx={{ 
          padding: '1.5rem',
          bgcolor: 'background.paper',
                    backgroundImage: 'none'
        }}>
          {/* Debug info */}
          {process.env.NODE_ENV === 'development' && (
            <Box sx={{ 
              marginBottom: '1rem', 
              padding: '0.75rem', 
              bgcolor: 'action.selected',
                    backgroundImage: 'none', 
              borderRadius: '0.5rem', 
              fontSize: '0.75rem',
              border: '1px solid',
                    borderColor: 'divider'
            }}>
              <Typography 
                variant="caption" 
                sx={{ 
                  color: 'text.secondary',
                  fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
                  fontSize: '0.75rem'
                }}
              >
                Debug: requires_sales_contact = {String(editForm.requires_sales_contact)} | 
                sales_contact_message = "{editForm.sales_contact_message}"
              </Typography>
            </Box>
          )}
          
          {editTab === 0 && (
            <Grid container spacing={2} sx={{ mt: 0.5 }} key={`form-${editTier?.id}-${formRefreshKey}`}>
              <Grid item xs={12}>
                <TextField 
                  label="Name" 
                  fullWidth 
                  value={editForm.name || ''} 
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  sx={{
                    '& .MuiInputBase-input': { 
                      color: 'text.primary',
                      fontSize: '0.875rem',
                      fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif'
                    },
                    '& .MuiInputLabel-root': { 
                      color: 'text.secondary',
                      fontSize: '0.875rem',
                      fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif'
                    },
                    '& .MuiOutlinedInput-notchedOutline': { 
                      borderColor: 'divider' 
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': { 
                      borderColor: 'primary.main' 
                    },
                    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'primary.main'
                    }
                  }} 
                />
              </Grid>
              <Grid item xs={12}>
                <TextField 
                  label="Tier Key" 
                  fullWidth 
                  value={editForm.tier_key || ''} 
                  disabled 
                  onChange={(e) => setEditForm({ ...editForm, tier_key: e.target.value })}
                  sx={{
                    '& .MuiInputBase-input': { 
                      color: 'text.secondary',
                      fontSize: '0.875rem',
                      fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif'
                    },
                    '& .MuiInputLabel-root': { 
                      color: 'text.secondary',
                      fontSize: '0.875rem',
                      fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif'
                    },
                    '& .MuiOutlinedInput-notchedOutline': { 
                      borderColor: 'divider' 
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': { 
                      borderColor: 'primary.main' 
                    }
                  }} 
                />
              </Grid>
              <Grid item xs={12}>
                <TextField 
                  label="Description" 
                  fullWidth 
                  required
                  value={editForm.description || ''} 
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                  error={!editForm.description}
                  helperText={!editForm.description ? "Description is required for Stripe product creation" : ""}
                  sx={{
                    '& .MuiInputBase-input': { 
                      color: 'text.primary',
                      fontSize: '0.875rem',
                      fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif'
                    },
                    '& .MuiInputLabel-root': { 
                      color: 'text.secondary',
                      fontSize: '0.875rem',
                      fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif'
                    },
                    '& .MuiOutlinedInput-notchedOutline': { 
                      borderColor: 'divider' 
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': { 
                      borderColor: 'primary.main' 
                    },
                    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'primary.main'
                    },
                    '& .MuiFormHelperText-root': { 
                      color: 'text.secondary',
                      fontSize: '0.75rem',
                      fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif'
                    }
                  }} 
                />
              </Grid>
              <Grid item xs={6}>
                <TextField 
                  type="number" 
                  label="Base Price" 
                  fullWidth 
                  value={editForm.base_price ?? ''} 
                  onChange={(e) => setEditForm({ ...editForm, base_price: e.target.value === '' ? '' as any : e.target.value })}
                  sx={{
                    '& .MuiInputBase-input': { 
                      color: 'text.primary',
                      fontSize: '0.875rem',
                      fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif'
                    },
                    '& .MuiInputLabel-root': { 
                      color: 'text.secondary',
                      fontSize: '0.875rem',
                      fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif'
                    },
                    '& .MuiOutlinedInput-notchedOutline': { 
                      borderColor: 'divider' 
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': { 
                      borderColor: 'primary.main' 
                    },
                    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'primary.main'
                    }
                  }} 
                />
              </Grid>
              <Grid item xs={6}>
                <TextField 
                  type="number" 
                  label="Price per User" 
                  fullWidth 
                  value={editForm.price_per_user ?? ''} 
                  onChange={(e) => setEditForm({ ...editForm, price_per_user: e.target.value === '' ? '' as any : e.target.value })}
                  sx={{
                    '& .MuiInputBase-input': { 
                      color: 'text.primary',
                      fontSize: '0.875rem',
                      fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif'
                    },
                    '& .MuiInputLabel-root': { 
                      color: 'text.secondary',
                      fontSize: '0.875rem',
                      fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif'
                    },
                    '& .MuiOutlinedInput-notchedOutline': { 
                      borderColor: 'divider' 
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': { 
                      borderColor: 'primary.main' 
                    },
                    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'primary.main'
                    }
                  }} 
                />
              </Grid>
              <Grid item xs={4}>
                <TextField 
                  type="number" 
                  label="Free Users" 
                  fullWidth 
                  value={editForm.free_users_limit ?? ''} 
                  onChange={(e) => setEditForm({ ...editForm, free_users_limit: e.target.value === '' ? '' as any : e.target.value })}
                  sx={{
                    '& .MuiInputBase-input': { 
                      color: 'text.primary',
                      fontSize: '0.875rem',
                      fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif'
                    },
                    '& .MuiInputLabel-root': { 
                      color: 'text.secondary',
                      fontSize: '0.875rem',
                      fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif'
                    },
                    '& .MuiOutlinedInput-notchedOutline': { 
                      borderColor: 'divider' 
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': { 
                      borderColor: 'primary.main' 
                    },
                    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'primary.main'
                    }
                  }} 
                />
              </Grid>
              <Grid item xs={4}>
                <TextField 
                  type="number" 
                  label="Max Users" 
                  fullWidth 
                  value={editForm.max_users ?? ''} 
                  onChange={(e) => setEditForm({ ...editForm, max_users: e.target.value === '' ? '' as any : e.target.value })}
                  sx={{
                    '& .MuiInputBase-input': { 
                      color: 'text.primary',
                      fontSize: '0.875rem',
                      fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif'
                    },
                    '& .MuiInputLabel-root': { 
                      color: 'text.secondary',
                      fontSize: '0.875rem',
                      fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif'
                    },
                    '& .MuiOutlinedInput-notchedOutline': { 
                      borderColor: 'divider' 
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': { 
                      borderColor: 'primary.main' 
                    },
                    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'primary.main'
                    }
                  }} 
                />
              </Grid>
              <Grid item xs={4}>
                <TextField 
                  type="number" 
                  label="Max Rooms" 
                  fullWidth 
                  value={editForm.max_rooms ?? ''} 
                  onChange={(e) => setEditForm({ ...editForm, max_rooms: e.target.value === '' ? '' as any : e.target.value })}
                  sx={{
                    '& .MuiInputBase-input': { 
                      color: 'text.primary',
                      fontSize: '0.875rem',
                      fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif'
                    },
                    '& .MuiInputLabel-root': { 
                      color: 'text.secondary',
                      fontSize: '0.875rem',
                      fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif'
                    },
                    '& .MuiOutlinedInput-notchedOutline': { 
                      borderColor: 'divider' 
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': { 
                      borderColor: 'primary.main' 
                    },
                    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'primary.main'
                    }
                  }} 
                />
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '1rem',
                  padding: '1rem',
                  bgcolor: 'action.hover',
                    backgroundImage: 'none',
                  borderRadius: '0.75rem',
                  border: '1px solid',
                    borderColor: 'divider'
                }}>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: 'text.secondary',
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif'
                    }}
                  >
                    Tier Color:
                  </Typography>
                  <input
                    type="color"
                    value={editForm.color || defaultTierColor}
                    onChange={(e) => setEditForm({ ...editForm, color: e.target.value })}
                    style={{ 
                      width: '2.5rem', 
                      height: '2.5rem', 
                      border: '2px solid',
                    borderColor: 'divider', 
                      borderRadius: '0.5rem',
                      cursor: 'pointer',
                      background: 'transparent',
                      transition: 'border-color 0.2s ease'
                    }}
                    onMouseEnter={(e) => (e.target as HTMLInputElement).style.borderColor = theme.palette.primary.main}
                    onMouseLeave={(e) => (e.target as HTMLInputElement).style.borderColor = theme.palette.divider}
                    aria-label="Pick tier color"
                  />
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: 'text.primary',
                      fontFamily: 'monospace',
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      bgcolor: 'action.selected',
                    backgroundImage: 'none',
                      padding: '0.25rem 0.5rem',
                      borderRadius: '0.375rem'
                    }}
                  >
                    {editForm.color || defaultTierColor}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel 
                  control={
                    <Switch 
                      checked={!!editForm.is_default} 
                      onChange={(e) => setEditForm({ ...editForm, is_default: e.target.checked })}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: 'primary.main',
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          bgcolor: 'primary.main',
                            backgroundImage: 'none',
                        },
                      }}
                    />
                  }
                  label={
                    <Typography 
                      sx={{ 
                        color: 'text.primary',
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif'
                      }}
                    >
                      Default Tier for New Users
                    </Typography>
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel 
                  control={
                    <Switch 
                      checked={!!editForm.is_active} 
                      onChange={(e) => setEditForm({ ...editForm, is_active: e.target.checked })}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: 'primary.main',
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          bgcolor: 'primary.main',
                            backgroundImage: 'none',
                        },
                      }}
                    />
                  }
                  label={
                    <Typography 
                      sx={{ 
                        color: 'text.primary',
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif'
                      }}
                    >
                      Active
                    </Typography>
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel 
                  control={
                    <Switch 
                      checked={!!editForm.requires_sales_contact} 
                      onChange={(e) => setEditForm({ ...editForm, requires_sales_contact: e.target.checked })}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: 'primary.main',
                        },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                          bgcolor: 'primary.main',
                            backgroundImage: 'none',
                        },
                      }}
                    />
                  }
                  label={
                    <Typography 
                      sx={{ 
                        color: 'text.primary',
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif'
                      }}
                    >
                      Requires Sales Contact
                    </Typography>
                  }
                />
              </Grid>
              {editForm.requires_sales_contact && (
                <Grid item xs={12}>
                  <TextField 
                    label="Sales Contact Message" 
                    fullWidth 
                    multiline
                    rows={3}
                    value={editForm.sales_contact_message || ''} 
                    onChange={(e) => setEditForm({ ...editForm, sales_contact_message: e.target.value })}
                    placeholder="Enter custom message for sales contact requirement..."
                    sx={{
                      '& .MuiInputBase-input': { 
                        color: 'text.primary',
                        fontSize: '0.875rem',
                        fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif'
                      },
                      '& .MuiInputLabel-root': { 
                        color: 'text.secondary',
                        fontSize: '0.875rem',
                        fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif'
                      },
                      '& .MuiOutlinedInput-notchedOutline': { 
                        borderColor: 'divider' 
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': { 
                        borderColor: 'primary.main' 
                      },
                      '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'primary.main'
                      }
                    }} 
                  />
                </Grid>
              )}
            </Grid>
          )}

          {editTab === 1 && (
            <>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  mb: 2,
                  pb: 2,
                  borderBottom: 1,
                  borderColor: 'divider',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: alpha(theme.palette.primary.main, 0.12),
                    backgroundImage: 'none',
                    color: 'primary.main',
                    borderRadius: '8px',
                    width: 36,
                    height: 36,
                    border: '1px solid',
                    borderColor: alpha(theme.palette.primary.main, 0.28),
                  }}
                >
                  <SettingsIcon sx={{ fontSize: '1.125rem' }} />
                </Box>
                <Typography
                  variant="h6"
                  sx={{
                    color: 'text.primary',
                    fontWeight: 600,
                    fontSize: '1.125rem',
                    lineHeight: 1.25,
                    fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
                    letterSpacing: '-0.01em',
                  }}
                >
                  Tier Permissions
                </Typography>
              </Box>

              {loadingPerms ? (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    py: 4,
                    px: 2,
                    bgcolor: 'action.hover',
                    backgroundImage: 'none',
                    borderRadius: '8px',
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <CircularProgress size={32} sx={{ color: 'primary.main', mb: 2 }} />
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                      fontSize: '0.875rem',
                      fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
                      fontWeight: 500,
                    }}
                  >
                    Loading permission definitions...
                  </Typography>
                </Box>
              ) : null}

              {!loadingPerms && permDefs && editTier ? (
                <TierPermissionsEditor
                  tierId={editTier.id}
                  definitions={permDefs}
                  initialPermissions={tierPerms}
                  onChange={setTierPerms}
                />
              ) : null}

              {!loadingPerms && !permDefs ? (
                <Alert severity="warning" sx={{ borderRadius: '8px' }}>
                  Failed to load permission definitions. Please try refreshing the data.
                </Alert>
              ) : null}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ 
          borderTop: '1px solid',
                    borderTopColor: 'divider',
          padding: '1rem 1.5rem',
          bgcolor: 'action.hover',
                    backgroundImage: 'none'
        }}>
          <Button 
            onClick={closeEdit}
            variant="outlined"
            sx={{
              color: 'text.secondary',
              borderColor: 'divider',
              fontWeight: 500,
              fontSize: '0.875rem',
              padding: '0.5rem 1rem',
              borderRadius: '0.5rem',
              textTransform: 'none',
              fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
              '&:hover': {
                borderColor: 'primary.main',
                color: 'primary.main',
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                    backgroundImage: 'none',
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            variant="contained"
            color="primary"
            onClick={saveEdit} 
            disabled={saving}
            sx={{
              minWidth: 140,
              ...((billingSx.adminBillingPrimaryCtaButtonSx(theme) as object) || {}),
            }}
          >
            {saving ? <CircularProgress size={18} color="inherit" /> : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Tier Confirmation Dialog */}
      <Dialog 
        open={deleteDialogOpen} 
        onClose={closeDeleteDialog} 
        maxWidth="md" 
        fullWidth
        PaperProps={{ 
          sx: { 
            bgcolor: 'background.paper',
                    backgroundImage: 'none', 
            border: '1px solid',
                    borderColor: 'divider', 
            borderRadius: '1rem', 
            boxShadow: theme.shadows[10],
            overflow: 'hidden'
          } 
        }}
      >
        <DialogTitle sx={{ 
          color: 'error.main', 
          fontWeight: 600, 
          borderBottom: `1px solid ${alpha(theme.palette.error.main, 0.35)}`, 
          bgcolor: alpha(theme.palette.error.main, 0.08),
          backgroundImage: 'none',
          padding: '1.5rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          fontSize: '1.25rem',
          lineHeight: '1.25',
          fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif'
        }}>
          <WarningIcon sx={{ color: 'error.main', fontSize: '1.5rem' }} />
          Delete Billing Tier
        </DialogTitle>
        <DialogContent sx={{ padding: '1.5rem', bgcolor: 'background.paper',
                    backgroundImage: 'none' }}>
          {loadingAffectedCompanies ? (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center', 
              padding: '3rem 2rem',
              bgcolor: 'action.hover',
                    backgroundImage: 'none',
              borderRadius: '0.75rem',
              border: '1px solid',
                    borderColor: 'divider'
            }}>
              <CircularProgress size={32} sx={{ color: 'primary.main', marginBottom: '1rem' }} />
              <Typography 
                variant="body2" 
                sx={{ 
                  color: 'text.secondary',
                  fontSize: '0.875rem',
                  fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
                  fontWeight: 500
                }}
              >
                Loading affected companies...
              </Typography>
            </Box>
          ) : (
            <>
              {/* Tier Information */}
              {tierToDelete && (
                <Box sx={{ 
                  padding: '1rem',
                  bgcolor: 'action.hover',
                    backgroundImage: 'none',
                  borderRadius: '0.75rem',
                  border: '1px solid',
                    borderColor: 'divider',
                  marginBottom: '1.5rem'
                }}>
                  <Typography 
                    variant="subtitle2" 
                    sx={{ 
                      color: 'text.secondary',
                      fontSize: '0.75rem',
                      fontWeight: 500,
                      fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
                      textTransform: 'uppercase',
                      marginBottom: '0.5rem'
                    }}
                  >
                    Tier to Delete
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <Box sx={{ 
                      width: '1rem', 
                      height: '1rem', 
                      borderRadius: '50%', 
                      bgcolor: tierToDelete.color || theme.palette.text.secondary,
                      border: `2px solid ${alpha(theme.palette.divider, 0.9)}`,
                    }} />
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        color: 'text.primary',
                        fontSize: '1.125rem',
                        fontWeight: 600,
                        fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif'
                      }}
                    >
                      {tierToDelete.name}
                    </Typography>
                    <Chip 
                      label={tierToDelete.tier_key} 
                      size="small" 
                      sx={{ 
                        bgcolor: 'action.selected',
                    backgroundImage: 'none',
                        color: 'text.primary',
                        fontSize: '0.75rem',
                        fontWeight: 500
                      }} 
                    />
                  </Box>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: 'text.secondary',
                      fontSize: '0.875rem',
                      fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
                      marginTop: '0.5rem'
                    }}
                  >
                    {tierToDelete.description}
                  </Typography>
                </Box>
              )}

              {/* Affected Companies Warning */}
              {affectedCompaniesInfo && affectedCompaniesInfo.affected_count && affectedCompaniesInfo.affected_count.total > 0 && (
                <Alert 
                  severity="warning"
                  icon={<WarningIcon sx={{ color: 'warning.main' }} />}
                  sx={{
                    bgcolor: alpha(theme.palette.warning.main, 0.12),
                    backgroundImage: 'none',
                    border: `1px solid ${alpha(theme.palette.warning.main, 0.45)}`,
                    borderRadius: '0.75rem',
                    marginBottom: '1.5rem',
                    '& .MuiAlert-message': {
                      color: 'warning.dark',
                      fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      width: '100%'
                    }
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, marginBottom: '0.5rem' }}>
                    {affectedCompaniesInfo.affected_count.total} Companies Will Be Affected
                  </Typography>
                  <Typography variant="body2" sx={{ marginBottom: '1rem' }}>
                    The following companies are currently using this tier and will be moved to the replacement tier you select:
                  </Typography>
                  {affectedCompaniesInfo.affected_companies && affectedCompaniesInfo.affected_companies.length > 0 && (
                    <Box sx={{ 
                      maxHeight: '10rem', 
                      overflowY: 'auto',
                      bgcolor: alpha(theme.palette.background.paper, 0.65),
                      backgroundImage: 'none',
                      borderRadius: '0.5rem',
                      padding: '0.5rem',
                      border: '1px solid',
                      borderColor: 'divider',
                    }}>
                      <List dense sx={{ padding: 0 }}>
                        {affectedCompaniesInfo.affected_companies.map((company) => (
                          <ListItem 
                            key={company.id} 
                            sx={{ 
                              padding: '0.25rem 0.5rem',
                              '&:hover': { bgcolor: 'action.hover', backgroundImage: 'none' }
                            }}
                          >
                            <ListItemText 
                              primary={company.name} 
                              secondary={`ID: ${company.id}`}
                              primaryTypographyProps={{ 
                                sx: { 
                                  fontSize: '0.875rem', 
                                  fontWeight: 500,
                                  color: 'text.primary'
                                } 
                              }}
                              secondaryTypographyProps={{ 
                                sx: { 
                                  fontSize: '0.75rem',
                                  color: 'text.secondary'
                                } 
                              }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}
                </Alert>
              )}

              {/* No Affected Companies */}
              {affectedCompaniesInfo && affectedCompaniesInfo.can_delete_safely && (
                <Alert 
                  severity="info"
                  sx={{
                    bgcolor: alpha(theme.palette.info.main, 0.1),
                    backgroundImage: 'none',
                    border: `1px solid ${alpha(theme.palette.info.main, 0.45)}`,
                    borderRadius: '0.75rem',
                    marginBottom: '1.5rem',
                    '& .MuiAlert-message': {
                      color: 'info.dark',
                      fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
                      fontSize: '0.875rem',
                      fontWeight: 500
                    }
                  }}
                >
                  No companies are currently using this tier. It can be safely deleted without affecting any workspaces.
                </Alert>
              )}

              {/* Replacement Tier Selection */}
              {affectedCompaniesInfo && affectedCompaniesInfo.affected_count && affectedCompaniesInfo.affected_count.total > 0 && (
                <Box sx={{ marginBottom: '1.5rem' }}>
                  <Typography 
                    variant="subtitle2" 
                    sx={{ 
                      color: 'text.primary',
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
                      marginBottom: '0.75rem'
                    }}
                  >
                    Select Replacement Tier *
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: 'text.secondary',
                      fontSize: '0.875rem',
                      fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
                      marginBottom: '1rem'
                    }}
                  >
                    All affected companies will be moved to the selected replacement tier. 
                    Managers and admins of affected companies will receive an email notification.
                  </Typography>
                  <FormControl fullWidth>
                    <InputLabel 
                      id="replacement-tier-label"
                      sx={{ 
                        color: 'text.secondary',
                        fontSize: '0.875rem',
                        fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif'
                      }}
                    >
                      Replacement Tier
                    </InputLabel>
                    <Select
                      labelId="replacement-tier-label"
                      value={replacementTierId}
                      label="Replacement Tier"
                      onChange={(e) => setReplacementTierId(e.target.value)}
                      sx={{
                        '& .MuiOutlinedInput-notchedOutline': { 
                          borderColor: 'divider' 
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': { 
                          borderColor: 'primary.main' 
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'primary.main'
                        },
                        '& .MuiSelect-select': {
                          fontSize: '0.875rem',
                          fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif'
                        }
                      }}
                    >
                      {getAvailableReplacementTiers().map((tier) => (
                        <MenuItem key={tier.id} value={tier.id}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                            <Box sx={{ 
                              width: '0.75rem', 
                              height: '0.75rem', 
                              borderRadius: '50%', 
                              bgcolor: tier.color || theme.palette.text.secondary,
                              border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
                            }} />
                            <Typography sx={{ fontWeight: 500 }}>{tier.name}</Typography>
                            <Chip 
                              label={tier.tier_key} 
                              size="small" 
                              sx={{ 
                                fontSize: '0.7rem',
                                height: '1.25rem',
                                bgcolor: 'action.selected',
                    backgroundImage: 'none'
                              }} 
                            />
                            {tier.is_default && (
                              <Chip 
                                label="Default" 
                                size="small" 
                                sx={{ 
                                  fontSize: '0.7rem',
                                  height: '1.25rem',
                                  bgcolor: 'info.main',
                                  backgroundImage: 'none',
                                  color: 'white'
                                }} 
                              />
                            )}
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              )}

              {/* Final Warning */}
              <Alert 
                severity="error"
                sx={{
                  bgcolor: alpha(theme.palette.error.main, 0.08),
                  backgroundImage: 'none',
                  border: '1px solid',
                  borderColor: alpha(theme.palette.error.main, 0.35),
                  borderRadius: '0.75rem',
                  '& .MuiAlert-message': {
                    color: 'error.main',
                    fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
                    fontSize: '0.875rem',
                    fontWeight: 500
                  }
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 600, marginBottom: '0.25rem' }}>
                  This action cannot be undone.
                </Typography>
                {affectedCompaniesInfo && affectedCompaniesInfo.affected_count && affectedCompaniesInfo.affected_count.total > 0 ? (
                  <Typography variant="body2">
                    All {affectedCompaniesInfo.affected_count.total} companies currently on "{tierToDelete?.name}" will be moved to the selected replacement tier. 
                    Their managers and admins will be notified via email.
                  </Typography>
                ) : (
                  <Typography variant="body2">
                    The tier "{tierToDelete?.name}" will be permanently marked as deleted.
                  </Typography>
                )}
              </Alert>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ 
          borderTop: '1px solid',
                    borderTopColor: 'divider',
          padding: '1rem 1.5rem',
          bgcolor: 'action.hover',
                    backgroundImage: 'none'
        }}>
          <Button 
            onClick={closeDeleteDialog}
            variant="outlined"
            disabled={deleting}
            sx={{
              color: 'text.secondary',
              borderColor: 'divider',
              fontWeight: 500,
              fontSize: '0.875rem',
              padding: '0.5rem 1rem',
              borderRadius: '0.5rem',
              textTransform: 'none',
              fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
              '&:hover': {
                borderColor: 'primary.main',
                color: 'primary.main',
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                    backgroundImage: 'none',
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleDeleteTier} 
            disabled={deleting || loadingAffectedCompanies || (
              (affectedCompaniesInfo?.affected_count?.total ?? 0) > 0 && 
              !replacementTierId
            )}
            color="error"
            sx={{
              minWidth: 180,
              fontWeight: 600,
              fontSize: '0.875rem',
              padding: '0.5rem 1rem',
              borderRadius: '0.5rem',
              textTransform: 'none',
              fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
              boxShadow: theme.shadows[2],
              '&:hover': { 
                bgcolor: 'error.dark',
                backgroundImage: 'none',
                boxShadow: theme.shadows[6],
                transform: 'translateY(-0.125rem)'
              },
              '&:disabled': {
                bgcolor: 'action.disabledBackground',
                    backgroundImage: 'none',
                color: 'text.disabled',
                transform: 'none',
                boxShadow: 'none'
              }
            }}
          >
            {deleting ? (
              <>
                <CircularProgress size={18} color="inherit" sx={{ mr: 1 }} />
                Deleting...
              </>
            ) : (
              affectedCompaniesInfo?.affected_count?.total && affectedCompaniesInfo.affected_count.total > 0
                ? 'Delete & Reassign Companies'
                : 'Delete Tier'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default AdminBillingSettings